import { NoMaxWidthTooltip } from '@centerpiece/components';
import { createFilterOptions, Chip, Box, Tooltip, Badge } from '@mui/material';
import {
  CorporateFare,
  PersonAdd,
  Person,
  Mail,
  DoNotDisturbOn,
  Pending,
  CheckCircle,
  Help,
} from '@mui/icons-material';
import { ContactTooltip } from 'components/ContactTooltip';
import { SubscriptionContact } from 'store/api/models';
import { useAllContacts } from 'utils/contacts.utils';
import { getIn, FormikHelpers } from 'formik';
import { useState } from 'react';

export const useContacts = ({
  name,
  label,
  disabled,
  setOpenContact,
}: {
  name: string;
  label: string;
  disabled?: boolean;
  setOpenContact?: Function;
}) => {
  const contacts = useAllContacts();
  return {
    name: name,
    itemType: 'autocomplete',
    options: contacts || [],
    multiple: true,
    disabled: disabled,
    // freeSolo: true,
    // freeSoloFormat: (value: any) => ({ id: 0, email: value, label: value }),
    jItemProps: {
      optionsFilter: createFilterOptions({
        stringify: (option: any) =>
          `${option?.firstname} ${option?.lastname} ${option?.email} ${option?.organization}`,
      }),
      addValue: async (
        name: any,
        newValue: any,
        values: any,
        formik: FormikHelpers<any>
      ) => {
        const names = newValue?.inputValue?.split(' ');
        // formik.setFieldValue(name, []);
        setOpenContact &&
          setOpenContact({
            open: true,
            callback: (contact: SubscriptionContact) => {
              formik.setFieldValue(name, [
                ...(getIn(values, name) || []),
                {
                  ...contact?.contact_r,
                  name: `${contact?.contact_r?.firstname} ${contact?.contact_r?.lastname}`,
                  role: contact?.role_r?.name,
                  organization:
                    contact?.organization ||
                    contact?.contact_r?.form?.organization,
                },
              ]);
            },
            close: () => {
              formik.setFieldValue(name, [
                ...(getIn(values, name) || [].filter((v: any) => !v.addValue)),
              ]);
            },
            initialValues: {
              firstname: names[0],
              lastname: name.length > 1 ? names[1] : '',
            },
          });
      },
      ...renderContactGroups(undefined, true),
      limit: false,
      renderTags: (value: any[] | undefined, getTagProps: any) => {
        return value?.map((option: any, index: number) => (
          <NoMaxWidthTooltip title={<ContactTooltip {...option} />}>
            <span>
              <Chip
                icon={
                  option?.partstat === 'ACCEPTED' ? (
                    <CheckCircle />
                  ) : option?.partstat === 'DECLINED' ? (
                    <DoNotDisturbOn />
                  ) : option?.partstat === 'Tentative' ? (
                    <Help />
                  ) : option?.partstat === 'NEEDS_ACTION' ? (
                    <Pending />
                  ) : undefined
                }
                variant="outlined"
                color="secondary"
                label={typeof option === 'string' ? option : option.name}
                size="small"
                {...getTagProps({ index })}
                {...(disabled ? { onDelete: undefined, disabled: false } : {})}
              />
            </span>
          </NoMaxWidthTooltip>
        ));
      },
      // helperText: (
      //   <Typography variant="caption" id="tableTitle" component="div">
      //     <IconButton size="small" disabled>
      //       <Add color="secondary" />
      //     </IconButton>
      //     Add recipients
      //   </Typography>
      // ),
    },
    label: label,
    gridProps: { xs: 12 },
  };
};

export const renderContactGroups = (role?: any, displayRole?: boolean) => ({
  groupBy: (option: any) =>
    option?.groupBy?.toUpperCase() ||
    option?.organization_r?.name?.toUpperCase() ||
    ' ',
  renderGroup: (params: any) => {
    return (
      <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }}>
        {params?.group && (
          <Box
            sx={{
              px: 1,
              backgroundColor: 'lightgray',
              borderRadius: '2px',
            }}
          >
            {params?.group?.toUpperCase() !== role?.name?.toUpperCase() ? (
              <Tooltip
                title={`Organization: ${
                  typeof params.group === 'string'
                    ? params?.group?.toUpperCase()
                    : params?.group?.name?.toUpperCase()
                }`}
              >
                <Box component={'span'} sx={{ fontWeight: 600 }}>
                  <CorporateFare
                    sx={{ fontSize: '12px', mr: 1 }}
                    color="secondary"
                  />
                  {typeof params.group === 'string'
                    ? params?.group?.toUpperCase()
                    : params?.group?.name?.toUpperCase()}
                </Box>
              </Tooltip>
            ) : (
              <Tooltip
                title={`Stakeholder: ${
                  typeof params.group === 'string'
                    ? params?.group?.toUpperCase()
                    : params?.group?.name?.toUpperCase()
                }`}
              >
                <Box component={'span'} sx={{ fontWeight: 600 }}>
                  <Badge sx={{ fontSize: '12px', mr: 1 }} color="secondary" />
                  {typeof params.group === 'string'
                    ? params?.group?.toUpperCase()
                    : params?.group?.name?.toUpperCase()}
                </Box>
              </Tooltip>
            )}
          </Box>
        )}
        {params.children}
      </Box>
    );
  },
  renderOption: (props: any, option: any) => {
    return (
      <Box
        component="li"
        sx={{ whiteSpace: 'nowrap', fontSize: '1em' }}
        {...props}
      >
        {option?.addValue ? (
          <>
            <Tooltip title="Add Contact">
              <PersonAdd fontSize="inherit" sx={{ mr: 1 }} />
            </Tooltip>
            {option.title}
          </>
        ) : (
          <>
            {(option?.firstname || option?.lastname) && (
              <span>
                <Person
                  sx={{
                    fontSize: '12px',
                    mr: 1,
                  }}
                  color="secondary"
                />
                {`${option?.firstname || ''} ${option?.lastname || ''}`}
              </span>
            )}
            {option?.email && (
              <i>
                <u>
                  <Mail
                    sx={{
                      fontSize: '12px',
                      mr: 1,
                      ml: option?.firstname || option?.lastname ? 1 : 0,
                    }}
                    color="secondary"
                  />
                  <small>{option?.email}</small>
                </u>
              </i>
            )}
            {!option?.groupBy ? (
              <>
                {option?.role && (
                  <i>
                    <Badge sx={{ fontSize: '12px', mx: 1 }} color="secondary" />
                    <small>{option?.role}</small>
                  </i>
                )}
              </>
            ) : (
              <>
                {!!displayRole && option?.role && (
                  <i>
                    <Badge sx={{ fontSize: '12px', mx: 1 }} color="secondary" />
                    <small>{option?.role}</small>
                  </i>
                )}
                {option?.organization_r?.name && (
                  <b>
                    <CorporateFare
                      sx={{ fontSize: '12px', mx: 1 }}
                      color="secondary"
                    />

                    <small>{option?.organization_r?.name?.toUpperCase()}</small>
                  </b>
                )}
              </>
            )}
          </>
        )}
      </Box>
    );
  },
});
